import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { usePlanning } from '../contexts/PlanningContext';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';
import { he } from 'date-fns/locale';

const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export default function CalendarPage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { plans } = usePlanning();
  const [currentDate, setCurrentDate] = useState(new Date());

  const weekdays = WEEKDAY_KEYS.map(key => t(`planning.weekdays.${key}`));

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  const getPlansForDay = (date: Date) => {
    return plans.filter(plan => isSameDay(new Date(plan.date), date));
  };

  const getActionItemsForDay = (date: Date) => {
    const items: { planId: string; planTitle: string; subjectId: string; item: any }[] = [];
    plans.forEach(plan => {
      plan.subjects.forEach(subject => {
        subject.actionItems.forEach(item => {
          if (item.dueDate && isSameDay(new Date(item.dueDate), date)) {
            items.push({
              planId: plan.id,
              planTitle: plan.title,
              subjectId: subject.id,
              item,
            });
          }
        });
      });
    });
    return items;
  };

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h1 className="page-title">{t('planning.calendarTitle')}</h1>
      </div>

      <div className="calendar-container">
        <div className="calendar-header">
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
            {format(currentDate, 'MMMM yyyy', { locale: he })}
          </h2>
          <div className="calendar-nav">
            <button className="btn btn-secondary" onClick={handlePrevMonth}>
              ←
            </button>
            <button className="btn btn-secondary" onClick={handleToday}>
              {t('planning.today')}
            </button>
            <button className="btn btn-secondary" onClick={handleNextMonth}>
              →
            </button>
          </div>
        </div>

        <div className="calendar-grid">
          {/* Day Headers */}
          {weekdays.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}

          {/* Calendar Days */}
          {calendarDays.map(day => {
            const dayPlans = getPlansForDay(day);
            const dayItems = getActionItemsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentDate);

            return (
              <div
                key={day.toISOString()}
                className={`calendar-day ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
              >
                <div className="calendar-day-number">
                  {format(day, 'd')}
                </div>

                {/* Plans */}
                {dayPlans.map(plan => (
                  <div
                    key={plan.id}
                    className="calendar-event"
                    style={{
                      background: plan.status === 'completed' ? 'var(--success-color)' :
                                  plan.status === 'active' ? 'var(--primary-color)' : 'var(--status-draft)',
                    }}
                    onClick={() => navigate(`/plans/${plan.id}`)}
                    title={plan.title}
                  >
                    📋 {plan.title}
                  </div>
                ))}

                {/* Action Items */}
                {dayItems.slice(0, 3).map(({ planId, item }) => (
                  <div
                    key={item.id}
                    className="calendar-event"
                    style={{
                      background: item.isCompleted ? 'var(--success-color)' :
                                  item.priority === 'high' ? 'var(--error-color)' :
                                  item.priority === 'medium' ? 'var(--warning-color)' : 'var(--info-color)',
                    }}
                    onClick={() => navigate(`/plans/${planId}`)}
                    title={item.description}
                  >
                    {item.isCompleted ? '✓' : '○'} {item.description}
                  </div>
                ))}

                {dayItems.length > 3 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', padding: '2px' }}>
                    +{dayItems.length - 3} {t('planning.more')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <div style={{ width: '16px', height: '16px', background: 'var(--primary-color)', borderRadius: '4px' }}></div>
              <span>{t('planning.activePlan')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <div style={{ width: '16px', height: '16px', background: 'var(--success-color)', borderRadius: '4px' }}></div>
              <span>{t('planning.completed')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <div style={{ width: '16px', height: '16px', background: 'var(--error-color)', borderRadius: '4px' }}></div>
              <span>{t('planning.highPriorityTask')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <div style={{ width: '16px', height: '16px', background: 'var(--warning-color)', borderRadius: '4px' }}></div>
              <span>{t('planning.mediumPriorityTask')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
